import { useMutation, useLazyQuery } from '@apollo/client'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link as RouterLink } from 'react-router-dom'

import { validations } from '../components/form'
import {
  Acknowledge,
  Button,
  Container,
  ContentBox,
  Fields,
  FormHelperText,
  Link,
  Row,
  Stack,
  Text,
} from '../components'
import { mutations, queries } from '../graphql'
import { USER_STATUS_ID } from '../constants'

export function ForgotPassword() {
  const {
    register,
    handleSubmit,
    formState,
    reset: resetForm,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const { errors = {} } = formState
  const [isGuestUser, setIsGuestUser] = useState(false)
  const [requestPasswordReset, { loading, data, reset: resetMutation }] = useMutation(
    mutations.user.requestPasswordReset,
    {
      onCompleted: () => {
        resetForm()
      },
    }
  )

  const [getUserByEmail] = useLazyQuery(queries.user.getUserByEmail)

  const onSubmit = async ({ email }) => {
    const { data: userData } = await getUserByEmail({ variables: { email } })
    // 2 scenarios where the user is directed to Sign Up
    // A) no User found with the entered email
    // B) User found with status = 1 (i.e. guest)
    if (!userData || userData?.getUserByEmail?.status === String(USER_STATUS_ID.guest)) {
      setIsGuestUser(true)
    } else {
      requestPasswordReset({ variables: { data: { email } } })
    }
  }

  const Success = useCallback(
    () => (
      <Acknowledge.Success
        title="Thanks!"
        content={
          <Text.Body>
            You have been emailed a link to reset your password. If you do not receive this email, please check your
            junk mail or email <Link href="mailto:support@givewise.ca">support@givewise.ca</Link> for assistance.
          </Text.Body>
        }
      >
        <Button color="info" onClick={resetMutation}>
          Back
        </Button>
        <Button to="/login">Log In</Button>
      </Acknowledge.Success>
    ),
    [resetMutation]
  )

  return (
    <Container maxWidth="sm">
      <Stack spacing={4}>
        {isGuestUser ? (
          <>
            <Text.H5 data-testid="guestUserSignUpTitle">Sign Up With Us!</Text.H5>
            <ContentBox border>
              <Stack spacing={4}>
                <Row justifyContent="flex-end" alignItems="center">
                  <Text.Body data-testid="contentText">
                    There is no password set up for this email address. Please click the Sign Up link below to create a
                    Giving Fund.
                  </Text.Body>
                </Row>
                <Row justifyContent="flex-end" alignItems="center">
                  <Text.AltBody>
                    Still need help? Contact{' '}
                    <Link href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`} data-testid="supportLink">
                      support@givewise.ca
                    </Link>
                  </Text.AltBody>

                  <Button
                    component={RouterLink}
                    to="/sign-up"
                    // state={{ afterLogin }}
                    size="large"
                    color="info"
                    data-testid="signUpButton"
                  >
                    Sign Up
                  </Button>
                </Row>
              </Stack>
            </ContentBox>
          </>
        ) : (
          <Acknowledge.Switch success={!!data && <Success />}>
            <Text.H5 data-testid="forgotPasswordTitle">Forgot Password?</Text.H5>
            <Text.Body data-testid="contentText">
              Please enter the email address you used to register your account, and we will send you instructions to
              reset your password.
            </Text.Body>

            <ContentBox border>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={4}>
                  <Fields.Text
                    inputProps={{ 'data-testid': 'email' }}
                    label="Your Email *"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    {...register('email', { ...validations.required, ...validations.email })}
                  />

                  <Row justifyContent="flex-end" alignItems="center">
                    <Text.AltBody>
                      Still need help? Contact{' '}
                      <Link href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`} data-testid="supportLink">
                        support@givewise.ca
                      </Link>
                    </Text.AltBody>

                    <Button type="submit" disabled={loading} data-testid="sendButton">
                      Send
                    </Button>
                    {errors.form && <FormHelperText error={!!errors.form}>{errors.form.message}</FormHelperText>}
                  </Row>
                </Stack>
              </form>
            </ContentBox>
          </Acknowledge.Switch>
        )}
        <Row justifyContent="flex-end" alignItems="center" px={4} m={4}>
          <Button component={RouterLink} to="/login" data-testid="backLoginLink">
            Back to Login
          </Button>
        </Row>
      </Stack>
    </Container>
  )
}
